import type { ProviderProgramContext } from './navigation/provider-type';
import type { ProviderType } from './navigation/nav-v2';
import type { ProgramFamily } from '@statura/experience-contract';

export interface OrgLabels {
  /** Whether tenant org type has resolved from cache or tenant config */
  isLoaded: boolean;
  /** Tenant operating model driving labels and workflow affordances */
  orgType: 'HOME_CARE' | 'RESIDENTIAL' | 'MIXED' | 'UNKNOWN';
  /** Current program-enrollment discovery scope; orgType never widens it. */
  providerType: ProviderType;
  /** Server-projected current tenant context; never reconstructed from labels. */
  providerProgramContext?: ProviderProgramContext;
  /** Exact selected current family; null means shared/unresolved workspace. */
  activeProgramFamily?: ProgramFamily | null;
  /** Changes whenever current authority or the selected lens changes. */
  programContextRevision?: string | null;
  /** Multi-family tenants remain shared-only until an exact family is selected. */
  programSelectionRequired?: boolean;
  /** "Client" for HC, "Resident" for residential */
  person: string;
  /** "Clients" for HC, "Residents" for residential */
  persons: string;
  /** "Office" for HC, "Facility" for residential */
  location: string;
  /** "Offices" for HC, "Facilities" for residential */
  locations: string;
  /** "Service Start Date" for HC, "Admitted" for residential */
  admittedLabel: string;
  /** Whether this is a home care org (for hiding residential-only widgets) */
  isHomeCare: boolean;
  /** Whether this is a residential org */
  isResidential: boolean;
}

const RESIDENTIAL_LABELS: OrgLabels = {
  isLoaded: false,
  orgType: 'RESIDENTIAL',
  providerType: 'UNKNOWN',
  activeProgramFamily: null,
  programContextRevision: null,
  programSelectionRequired: false,
  person: 'Resident',
  persons: 'Residents',
  location: 'Facility',
  locations: 'Facilities',
  admittedLabel: 'Admitted',
  isHomeCare: false,
  isResidential: true,
};

const HOME_CARE_LABELS: OrgLabels = {
  isLoaded: false,
  orgType: 'HOME_CARE',
  providerType: 'UNKNOWN',
  activeProgramFamily: null,
  programContextRevision: null,
  programSelectionRequired: false,
  person: 'Client',
  persons: 'Clients',
  location: 'Office',
  locations: 'Offices',
  admittedLabel: 'Service Start Date',
  isHomeCare: true,
  isResidential: false,
};

const NEUTRAL_LABELS: OrgLabels = {
  isLoaded: false,
  orgType: 'UNKNOWN',
  providerType: 'UNKNOWN',
  activeProgramFamily: null,
  programContextRevision: null,
  programSelectionRequired: false,
  person: 'Person',
  persons: 'People',
  location: 'Service location',
  locations: 'Service locations',
  admittedLabel: 'Service start',
  isHomeCare: false,
  isResidential: false,
};

/** Resolve org type string to the correct label set */
export function resolveOrgLabels(orgType: string | undefined | null, isLoaded = true): OrgLabels {
  if (orgType === 'HOME_CARE') return { ...HOME_CARE_LABELS, isLoaded };
  if (orgType === 'RESIDENTIAL') return { ...RESIDENTIAL_LABELS, isLoaded };
  if (orgType === 'MIXED') return { ...NEUTRAL_LABELS, isLoaded, orgType: 'MIXED' };
  return { ...NEUTRAL_LABELS, isLoaded };
}

export function resolveOrgLabelsForProgramLens(
  labels: OrgLabels,
  providerType: ProviderType,
): OrgLabels {
  const vocabulary =
    providerType === 'SAH'
      ? resolveOrgLabels('HOME_CARE', labels.isLoaded)
      : providerType === 'RAC'
        ? resolveOrgLabels('RESIDENTIAL', labels.isLoaded)
        : resolveOrgLabels(undefined, labels.isLoaded);
  return { ...vocabulary, orgType: labels.orgType, providerType };
}

/** Lowercase person label for inline copy ("this resident"). */
export function thisPersonLabel(person: string): string {
  return person.toLowerCase();
}

/** Inline possessive phrase ("this resident"). */
export function thisPersonPhrase(person: string): string {
  return `this ${thisPersonLabel(person)}`;
}

export function personNotFoundMessage(person: string, punctuate = false): string {
  return punctuate ? `${person} not found.` : `${person} not found`;
}

export function personRequiredMessage(person: string): string {
  return `A ${thisPersonLabel(person)} is required.`;
}

export function personIdRequiredMessage(person: string): string {
  return `${person} is required`;
}

export function personRequiredToAdmitMessage(person: string): string {
  return `A ${thisPersonLabel(person)} is required to admit.`;
}

export function waitlistOnlyAdmitMessage(person: string): string {
  return `Only a ${thisPersonLabel(person)} currently on the waitlist can be admitted.`;
}

export function personNotFoundInOrgMessage(person: string): string {
  return `${person} not found in your organisation`;
}

export function unableToVerifyPersonMessage(person: string): string {
  return `Unable to verify ${thisPersonLabel(person)} — please try again.`;
}

export function onboardingPackNotFoundForPersonMessage(person: string): string {
  return `${person} onboarding pack was not found for ${thisPersonPhrase(person)}.`;
}

export function supplementBelongsToPersonMessage(person: string): string {
  return `That supplement does not belong to ${thisPersonPhrase(person)}.`;
}

export function newPersonRegisteredAssessmentDescription(person: string): string {
  return `A new ${thisPersonLabel(person)} has been registered. Complete their initial assessment so a care plan can be built from it.`;
}

export function contributionBasisRequiredMessage(person: string): string {
  return `Record the Services Australia contribution basis for ${thisPersonPhrase(person)} before generating the service agreement.`;
}

export function personRecordPhrase(person: string): string {
  return `${thisPersonLabel(person)} record`;
}

export function personCouldNotBeFoundMessage(person: string): string {
  return `That ${thisPersonLabel(person)} could not be found.`;
}

export function personBelongsToOtherOrgMessage(person: string): string {
  return `That ${thisPersonLabel(person)} belongs to another organisation.`;
}

export function personNotFoundOrNoAccessMessage(person: string): string {
  return `${person} not found or you do not have access to ${thisPersonPhrase(person)}.`;
}

export function personRequiredForReferralMessage(person: string): string {
  return `A ${thisPersonLabel(person)} is required for the referral.`;
}

export function restorativeProgrammeNotFoundForPersonMessage(person: string): string {
  return `The linked restorative programme was not found for ${thisPersonPhrase(person)}.`;
}

export function sourceAssessmentNotFoundForPersonMessage(person: string): string {
  return `The source assessment was not found for ${thisPersonPhrase(person)}.`;
}

export function activeProtocolNotFoundForPersonMessage(person: string): string {
  return `No active protocol exists for ${thisPersonPhrase(person)} and condition.`;
}

export function careTeamRoleAlreadyAssignedMessage(roleLabel: string, person: string): string {
  return `${roleLabel} is already assigned for ${thisPersonPhrase(person)}. Reassign the existing holder instead.`;
}

export function carePlanNotBelongingToPersonMessage(person: string): string {
  return `The selected care plan does not belong to ${thisPersonPhrase(person)}.`;
}

export function serviceLineNotBelongingToPersonMessage(person: string): string {
  return `The selected service line does not belong to ${thisPersonPhrase(person)}.`;
}

export function selectActiveFundingAuthForPersonMessage(person: string): string {
  return `Select an active funding authorisation for ${thisPersonPhrase(person)}.`;
}

export function failedToLoadPersonSectionMessage(person: string, section: string): string {
  return `Failed to load ${thisPersonLabel(person)} ${section}.`;
}

export function noActiveCarePlanForPersonMessage(person: string): string {
  return `No active care plan for ${thisPersonPhrase(person)}`;
}

export function affectedPersonLabel(person: string): string {
  return `affected ${thisPersonLabel(person)}`;
}

export function localiseThisClientCopy(text: string, person: string): string {
  return text.replace(/\bthis client\b/gi, thisPersonPhrase(person));
}

export function unknownPersonLabel(person: string): string {
  return `Unknown ${thisPersonLabel(person)}`;
}

/** Org-neutral fallback when person labels are not in scope (server-only helpers). */
export const NEUTRAL_UNKNOWN_PERSON = 'Unknown person';

/** Org-neutral fallback when no person is linked (server-only helpers). */
export const NEUTRAL_NO_PERSON = 'No person assigned';

/** Render-time normaliser for in-app help article copy. */
export function localiseHelpCopy(
  text: string,
  labels: Pick<OrgLabels, 'person' | 'persons'>,
): string {
  const { person, persons } = labels;
  const pl = thisPersonLabel(person);
  const record = personRecordPhrase(person);
  const tp = thisPersonPhrase(person);
  const affected = affectedPersonLabel(person);

  return text
    .replace(/\bthe client record\b/gi, `the ${record}`)
    .replace(/\bclient record\b/gi, record)
    .replace(/\bclient detail page\b/gi, `${pl} detail page`)
    .replace(/\bclient hub\b/gi, `${pl} hub`)
    .replace(/\bOpen client hub\b/gi, `Open ${pl} hub`)
    .replace(/\bclient-specific\b/gi, `${pl}-specific`)
    .replace(/\bfor this client\b/gi, `for ${tp}`)
    .replace(/\bthis client\b/gi, tp)
    .replace(/\baffected client\b/gi, affected)
    .replace(/\bthe affected client\b/gi, `the ${affected}`)
    .replace(/\bthe client\b/gi, `the ${pl}`)
    .replace(/\ba client\b/gi, `a ${pl}`)
    .replace(/\bclients\b/gi, persons.toLowerCase())
    .replace(/\bClient\b/g, person)
    .replace(/\bClients\b/g, persons);
}

export function signatoryNotPersonContactMessage(person: string): string {
  const label = thisPersonLabel(person);
  return `The selected signer isn't recorded as a contact for ${thisPersonPhrase(person)}. Add them as the ${label}'s representative first.`;
}

export function retryPersonRecordBeforeDeliveryHint(person: string): string {
  return `Support at Home status could not be loaded. Retry the ${personRecordPhrase(person)} before making delivery decisions.`;
}

export function advanceCarePlanFollowUpFromRecordMessage(
  person: string,
  variant: 'upload' | 'followup' | 'onfile',
): string {
  const record = personRecordPhrase(person);
  if (variant === 'upload') {
    return `Advance Care Directive / Plan evidence was marked hard copy / wet ink; upload or register it from the ${record}.`;
  }
  if (variant === 'onfile') {
    return `Advance Care Directive / Plan was marked on file at intake; obtain or verify the directive document and register it from the ${record} (or Advance Care Plans).`;
  }
  return `Advance Care Directive / Plan evidence is expected from family, representative, hospital, or GP; follow up from the ${record}.`;
}

export function waitlistAdmitReviewRecordMessage(person: string): string {
  return `The waitlist referral could not be admitted. Review the ${personRecordPhrase(person)} and try again.`;
}

export function linkedPersonRecordMismatchMessage(person: string): string {
  return `This login belongs to a different tenant than the linked ${personRecordPhrase(person)}. Sign out and use the correct invitation, or contact your provider to correct the access link.`;
}

/** Alias for help + page-customisation admin copy. */
export const localisePageCustomisationCopy = localiseHelpCopy;
